import React, { useCallback, useState, useMemo } from 'react';
import { Grid, makeStyles, TextField } from '@material-ui/core';
import { useAtom } from 'jotai';

import { FilterOptionType } from '@clef/shared/types';
import { Dropdown, DropdownProps, Button } from '@clef/client-library';

import { appliedFiltersAtom } from '@/uiStates/mediaFilter';

const useStyles = makeStyles(theme => ({
  textButton: {
    minWidth: 'auto',
    marginRight: theme.spacing(2),
  },
  dropdownPanel: {
    minWidth: 150,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  operationValueContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  inputRoot: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
  input: {
    paddingTop: 9,
    paddingBottom: 9,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    fontSize: '0.75rem',
  },
}));

export type FilterDropdownRawTextProps = {
  customizedSelector: (applied: boolean, open: boolean) => React.ReactNode;
  filterOption: FilterOptionType;
  // To be passed to Dropdown
  placement?: DropdownProps['placement'];
  extraGutter?: DropdownProps['extraGutter'];
  filterMappingKey: string | number;
};

export const FilterDropdownRawText: React.FC<FilterDropdownRawTextProps> = ({
  customizedSelector,
  filterOption,
  placement,
  extraGutter,
  filterMappingKey,
}) => {
  const styles = useStyles();
  const { filterName, filterType, fieldId } = filterOption;
  const [appliedFilters, setAppliedFilters] = useAtom(appliedFiltersAtom);
  const curAppliedFilters = useMemo(
    () => appliedFilters[filterMappingKey] ?? {},
    [appliedFilters, filterMappingKey],
  );

  const [textValue, setTextValue] = useState<string>(
    curAppliedFilters[filterName] ? String(curAppliedFilters[filterName].v) : '',
  );

  const applyRawTextFilter = useCallback(() => {
    const newCurAppliedFilters = { ...curAppliedFilters };
    newCurAppliedFilters[filterName] = {
      o: 'LIKE',
      v: textValue,
      t: filterType,
      fi: fieldId,
    };
    setAppliedFilters(prev => ({
      ...prev,
      [filterMappingKey]: newCurAppliedFilters,
    }));
  }, [
    curAppliedFilters,
    filterName,
    textValue,
    filterType,
    fieldId,
    setAppliedFilters,
    filterMappingKey,
  ]);

  const onValueChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target?.value || '');
  }, []);

  return (
    <Dropdown
      placement={placement}
      extraGutter={extraGutter}
      dropdown={
        <div className={styles.dropdownPanel}>
          <div className={styles.operationValueContainer}>
            <TextField
              variant="outlined"
              onKeyPress={ev => {
                if (ev.key === 'Enter') {
                  ev.preventDefault();
                  applyRawTextFilter();
                }
              }}
              fullWidth
              autoFocus
              onChange={onValueChange}
              value={textValue}
              inputProps={{
                className: styles.input,
                'data-testid': 'filter-raw-text-input',
              }}
              classes={{
                root: styles.inputRoot,
              }}
              size="small"
              data-testid="text-filter-value-field"
            />
          </div>
          {/* Clear all options */}
          <Grid container justifyContent="flex-end">
            <Button
              color="primary"
              size="small"
              className={styles.textButton}
              onClick={applyRawTextFilter}
              disabled={!textValue}
              id="text-filter-apply-btn"
            >
              {t('Search')}
            </Button>
            <Button
              color="secondary"
              size="small"
              className={styles.textButton}
              onClick={() => {
                const newCurAppliedFilters = { ...curAppliedFilters };
                delete newCurAppliedFilters[filterName];
                setAppliedFilters(prev => ({
                  ...prev,
                  [filterMappingKey]: newCurAppliedFilters,
                }));
              }}
              disabled={!appliedFilters[filterMappingKey]?.[filterName]}
              id="text-filter-clear-btn"
            >
              {t('Clear')}
            </Button>
          </Grid>
        </div>
      }
    >
      {open => customizedSelector(!!appliedFilters[filterMappingKey]?.[filterName], open)}
    </Dropdown>
  );
};

export default FilterDropdownRawText;
